import React, { useState, useEffect } from 'react';
import { Modal, Button, InputTextArea, Text, ModalInner, InfoMessage } from 'components';
import { InquiryDetail } from 'types/inquiry';
import { getInquiryDetail, answerInquiry, updateInquiryStatus } from 'api/inquiry';
import { getStatusConfig, inquiryTypeMap } from './utils';
import { Message } from 'types/message';
import { CheckCircleIcon, ExclamationCircleIcon } from 'assets/svgs';
import dayjs from 'dayjs';

interface InquiryModalProps {
  isOpen: boolean;
  inquiryId: number | null;
  onClose: () => void;
  onUpdate: () => void;
}

// 작성자 타입 매핑 (utils.ts에서 import한 inquiryTypeMap 사용)
const writerTypeMap = {
  MEMBER: '회원',
  GUEST: '비회원'
};

export const InquiryModal = ({ isOpen, inquiryId, onClose, onUpdate }: InquiryModalProps) => {
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [answerContent, setAnswerContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // 알림 모달 상태
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning'>('error');
  
  // 토스트 팝업 상태
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);

  // 문의 상세 데이터 로드
  useEffect(() => {
    if (isOpen && inquiryId) {
      setLoading(true);
      getInquiryDetail(inquiryId)
        .then(data => {
          setInquiry(data);
          setAnswerContent(data.answerContents || '');
        })
        .catch(error => {
          console.error('문의 상세 조회 실패:', error);
          showAlert('문의 정보를 불러오는데 실패했습니다.');
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, inquiryId]);

  // 알림 모달 함수
  const showAlert = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setIsAlertModalOpen(true);
  };

  const closeAlert = () => {
    setIsAlertModalOpen(false);
    setAlertMessage('');
  };

  // 토스트 팝업 함수
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const newMessage: Message = {
      index: messageArrayCount,
      icon: type === 'success' ? <CheckCircleIcon /> : <ExclamationCircleIcon />,
      text: message,
      bgClassName: type === 'success' ? 'bg-[#2AC111]' : 'bg-[#F30808]'
    };
    
    setMessageArray(prev => [...prev, newMessage]);
    setTimeout(() => onClickDeleteButton(messageArrayCount), 3000);
    setMessageArrayCount(prev => prev + 1);
  };

  const onClickDeleteButton = (index: number) => {
    setMessageArray(prev => prev.filter((message: any) => message.index !== index));
  };

  // 모달 닫기
  const handleClose = () => {
    setInquiry(null);
    setAnswerContent('');
    onClose();
  };

  // 답변 저장
  const handleSaveAnswer = async () => {
    if (!inquiry || !answerContent.trim()) {
      showAlert('답변 내용을 입력해주세요.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await answerInquiry({
        inquiryId: inquiry.inquiryId,
        answerContents: answerContent.trim()
      });
      
      showToast('답변이 성공적으로 저장되었습니다.', 'success');
      onUpdate();
      handleClose();
    } catch (error) {
      console.error('답변 저장 실패:', error);
      showAlert('답변 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 상태 변경
  const handleStatusChange = async (status: 'PENDING' | 'COMPLETED') => {
    if (!inquiry) return;

    setSubmitting(true);
    try {
      await updateInquiryStatus(inquiry.inquiryId, status);
      showToast(`상태가 ${status === 'COMPLETED' ? '완료' : '대기중'}으로 변경되었습니다.`, 'success');
      onUpdate();
      handleClose();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      showAlert('상태 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpened={isOpen}>
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b">
          <Text size="heading2" weight="semibold">
            문의 상세 #{inquiry?.inquiryId}
          </Text>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 내용 */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : inquiry ? (
            <div className="space-y-6">
              {/* 문의자 정보 */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <Text size="body1" weight="semibold" className="text-gray-800">
                  문의자 정보
                </Text>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Text size="small" color="secondary">이름</Text>
                    <Text size="body2">{inquiry.writerName}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">이메일</Text>
                    <Text size="body2">{inquiry.writerEmail}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">작성자 유형</Text>
                    <Text size="body2">{writerTypeMap[inquiry.inquiryWriterType]}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">조직/기관</Text>
                    <Text size="body2">{inquiry.organization || '-'}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">국가</Text>
                    <Text size="body2">{inquiry.country || '-'}</Text>
                  </div>
                </div>
              </div>

              {/* 문의 정보 */}
              <div className="space-y-3">
                <Text size="body1" weight="semibold" className="text-gray-800">
                  문의 정보
                </Text>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Text size="small" color="secondary">문의 유형</Text>
                    <Text size="body2">{inquiryTypeMap[inquiry.inquiryType]}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">작성일</Text>
                    <Text size="body2">{dayjs(inquiry.createdDate).format('YYYY-MM-DD HH:mm:ss')}</Text>
                  </div>
                  <div>
                    <Text size="small" color="secondary">상태</Text>
                    <div className="mt-1">
                      {(() => {
                        const config = getStatusConfig(inquiry.inquiryStatus, inquiry.isAnswered);
                        return (
                          <span 
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
                          >
                            {config.text}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* 문의 내용 */}
              <div className="space-y-3">
                <Text size="body1" weight="semibold" className="text-gray-800">
                  문의 내용
                </Text>
                <div className="bg-gray-50 rounded-lg p-4">
                  <Text size="body2" className="whitespace-pre-wrap">
                    {inquiry.inquiryContents}
                  </Text>
                </div>
              </div>

              {/* 답변 섹션 */}
              {inquiry.isAnswered && inquiry.answerContents ? (
                // 답변완료된 경우 - 답변 이력만 표시
                <div className="space-y-3">
                  <Text size="body1" weight="semibold" className="text-gray-800">
                    답변 이력
                  </Text>
                  <div>
                    <Text size="small" color="secondary" className="mb-2">
                      답변일: {inquiry.answeredDate ? dayjs(inquiry.answeredDate).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </Text>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <Text size="body2" className="whitespace-pre-wrap">
                        {inquiry.answerContents}
                      </Text>
                    </div>
                  </div>
                </div>
              ) : (
                // 답변하지 않은 경우 - 답변 작성 필드 표시
                <div className="space-y-3">
                  <Text size="body1" weight="semibold" className="text-gray-800">
                    답변 작성
                  </Text>
                  <InputTextArea
                    value={answerContent}
                    onChange={(value: string) => setAnswerContent(value)}
                    placeholder="답변 내용을 입력해주세요..."
                    className="w-full"
                    inputClassName="min-h-[200px]"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              문의 정보를 불러올 수 없습니다.
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
            <Button 
              text="닫기"
              type="secondary" 
              onClick={handleClose} 
              disabled={submitting}
            />
            {/* 답변하지 않은 경우에만 답변 저장 버튼 표시 */}
            {inquiry && !inquiry.isAnswered && (
            <Button
                text={submitting ? "저장 중..." : "답변 저장 및 메일 발송"}
                type="primary"
                onClick={handleSaveAnswer}
                disabled={submitting || !answerContent.trim()}
              />
            )}
        </div>
      </div>

      {/* 알림 모달 */}
      {isAlertModalOpen && (
        <Modal isOpened={true}>
          <ModalInner 
            className="md:w-[400px]" 
            title={alertType === 'success' ? 'Success' : alertType === 'warning' ? 'Warning' : 'Error'} 
            onClickClose={closeAlert}
          >
            <div className="p-4 pt-0">
              <p className="text-body1 text-center">{alertMessage}</p>
            </div>
            <div className="flex w-full justify-center bg-bg-transparent_gray_light p-4">
              <Button 
                className="h-9" 
                text="확인" 
                onClick={closeAlert} 
                type={alertType === 'success' ? 'primary' : 'secondary'} 
                size="large" 
              />
            </div>
          </ModalInner>
        </Modal>
      )}
      
      {/* 토스트 팝업 */}
      <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
    </Modal>
  );
};
